import * as React from 'react';
import { debounce, isFunction } from '@agilepoint/utils-common';
import { CarouselItemWrapper } from './parts/carousel.item.wrapper';
import { CarouselNavigation } from './parts/navigation';
import { IStackedCardCarouselProps } from './types';
import { IItemProps, ItemsWrapper } from '../../common/carousel-item';
import { CarouselContext } from '../../common/carousel-context';
import { CarouselWrapper } from '../../common/wrapper';
import { ListIterator } from '../../../../utils';

interface ICarouselState {
    activeKey?: React.Key;
    itemsToRender: Array<React.Key | undefined>;
}

export class StackedCardCarousel extends React.PureComponent<IStackedCardCarouselProps, ICarouselState> {
    static defaultProps: Partial<IStackedCardCarouselProps> = {
        navigationType: 'arrow',
        orientation: 'vertical',
        indicatorPosition: 'right'
    };

    private containerRef: React.RefObject<HTMLDivElement>;
    private autoCycleTimer = 0;
    private removeInactiveItemTimer = 0;
    private itemsMap = new Map<React.Key, IItemProps>();
    private itemsIterator: ListIterator<React.Key> = new ListIterator<React.Key>([]);

    static getDerivedStateFromProps(props: IStackedCardCarouselProps) {
        const { selectedId } = props;
        if (selectedId !== undefined) {
            return {
                activeKey: selectedId,
                itemsToRender: [selectedId]
            };
        }
        return null;
    }

    private updateComponent = debounce(() => {
        const { activeKey } = this.state;
        const newKeys = Array.from(this.itemsMap.keys());
        this.itemsIterator.updateList(newKeys);
        if (activeKey === undefined) {
            this.itemsIterator.next();
            return;
        }
        this.itemsIterator.find(key => key === activeKey);
        this.forceUpdate();
    }, 100, this);

    constructor(props: IStackedCardCarouselProps) {
        super(props);
        const { defaultSelectedId, selectedId, loop, elementRef } = this.props;
        const defaultId = selectedId ?? defaultSelectedId;
        this.state = {
            activeKey: defaultId,
            itemsToRender: [defaultId]
        };
        this.containerRef = elementRef ?? React.createRef<HTMLDivElement>();
        this.updateItertaorOptions({ loop });
    }

    componentDidMount() {
        const { autoCycle } = this.props;
        if (autoCycle) {
            this.startAutoCycle();
        }
        this.itemsIterator.subscribeToOnChange(this._onChange);
    }

    componentWillUnmount() {
        clearInterval(this.autoCycleTimer);
        this.itemsIterator.unsubscribeToOnChange(this._onChange);
    }

    componentDidUpdate(prevProps: IStackedCardCarouselProps) {
        const { loop, selectedId } = this.props;
        this.updateRemoveItemTimer();
        this.updateAutoCycleTimer(prevProps);
        if (prevProps.loop !== loop) {
            this.updateItertaorOptions({ loop });
        }
        if (selectedId !== undefined && this.itemsIterator.getValue() !== selectedId) {
            this.itemsIterator.find(key => key === selectedId);
        }
    }

    render() {
        const { className } = this.props;
        return (
            <CarouselWrapper className={className} ref={this.containerRef}>
                {this.onRenderItems()}
                {this.renderActiveItem()}
                {this.renderNavigator()}
            </CarouselWrapper>
        );
    }

    private addItem = (props: IItemProps) => {
        const { id } = props;
        this.itemsMap.set(id!, props);
        this.updateComponent();
    }

    private removeItem = (key: React.Key) => {
        this.itemsMap.delete(key!);
        this.updateComponent();
    }

    private onRenderItems() {
        const { children } = this.props;
        return (
            <CarouselContext.Provider value={{ addItem: this.addItem, removeItem: this.removeItem }}>
                {children}
            </CarouselContext.Provider>
        );
    }

    private renderActiveItem() {
        const { orientation, navigationType, indicatorPosition } = this.props;
        const { itemsToRender } = this.state;
        const isHorizontal = orientation === 'horizontal';
        const shouldUseDots = navigationType === 'dots';

        return (
            <ItemsWrapper
                alignItems="center"
                isHorizontal={isHorizontal}
                shouldUseDots={shouldUseDots}
                indicatorPosition={indicatorPosition}>
                {
                    itemsToRender.map(this.onItemRender)
                }
            </ItemsWrapper >
        );
    }

    private onItemRender = (key?: React.Key) => {
        const { activeKey } = this.state;
        const { id, ...itemProps } = this.itemsMap.get(key!) || {};
        return itemProps && key ? (
            <CarouselItemWrapper
                key={key}
                $isActive={key === activeKey}
                {...itemProps} />
        ) : null;
    }

    private renderNavigator() {
        const { loop = false, indicatorPosition, navigationType, orientation } = this.props;
        const { activeKey } = this.state;
        return (
            <CarouselNavigation
                activeKey={activeKey}
                itemsIterator={this.itemsIterator}
                type={navigationType}
                isHorizontal={orientation === 'horizontal'}
                loop={loop}
                indicatorPosition={indicatorPosition} />
        );
    }

    private startAutoCycle() {
        const { autoCycleInterval = 4000 } = this.props;
        clearInterval(this.autoCycleTimer);
        this.autoCycleTimer = setInterval(this.onAutoCycle, autoCycleInterval);
    }

    private onAutoCycle = () => {
        this.itemsIterator.next();
    }

    private _onChange = (activeKey: React.Key) => {
        const { onChange } = this.props;
        const { activeKey: prevActiveKey } = this.state;
        this.setState({
            activeKey,
            itemsToRender: (activeKey === prevActiveKey) ? [activeKey] : [activeKey, prevActiveKey]
        });
        if (isFunction(onChange) && prevActiveKey !== activeKey) {
            onChange(activeKey);
        }
    }

    private removeInActiveItem = () => {
        const { itemsToRender, activeKey } = this.state;
        if (itemsToRender.length > 1) {
            this.setState({
                itemsToRender: itemsToRender.filter(key => key === activeKey)
            });
        }
    }

    private updateItertaorOptions(options: { loop?: boolean }) {
        this.itemsIterator.updateOptions(options);
    }

    private updateRemoveItemTimer() {
        clearTimeout(this.removeInactiveItemTimer);
        this.removeInactiveItemTimer = setTimeout(this.removeInActiveItem, 1000);
    }

    private updateAutoCycleTimer(prevProps: IStackedCardCarouselProps) {
        const { autoCycleInterval, autoCycle } = this.props;
        if (prevProps.autoCycle === autoCycle && prevProps.autoCycleInterval === autoCycleInterval) {
            return;
        }
        if (autoCycle === false) {
            clearInterval(this.autoCycleTimer);
            return;
        }
        this.startAutoCycle();
    }
}
